import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Trophy, ChevronRight, Star, Calendar, Camera } from 'lucide-react';
import { MatchCard } from '../components/common/MatchCard';
import { Button } from '../components/ui/button';
import { getMatches, getTeams, getLogo, getSettings, getChampions, getMatchPhotos } from '../lib/api';

const HERO_IMAGE = 'https://images.pexels.com/photos/28827841/pexels-photo-28827841.jpeg';

export const HomePage = () => {
  const [upcomingMatches, setUpcomingMatches] = useState([]);
  const [teams, setTeams] = useState([]);
  const [settings, setSettings] = useState(null);
  const [champions, setChampions] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [logoUrl, setLogoUrl] = useState('/logo512.png');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [matchesRes, teamsRes, logoRes, settingsRes, champsRes, photosRes] = await Promise.all([
          getMatches(),
          getTeams(),
          getLogo().catch(() => ({ data: { has_logo: false } })),
          getSettings().catch(() => ({ data: {} })),
          getChampions().catch(() => ({ data: [] })),
          getMatchPhotos(8).catch(() => ({ data: [] })),
        ]);

        const allMatches = matchesRes.data;

        const upcoming = allMatches
          .filter(m => !m.is_played)
          .sort((a, b) => new Date(a.match_date) - new Date(b.match_date))
          .slice(0, 4);

        setUpcomingMatches(upcoming);
        setTeams(teamsRes.data);
        setSettings(settingsRes.data || {});
        setChampions(champsRes.data || []);
        setPhotos(photosRes.data || []);

        // Set logo URL
        if (logoRes.data?.has_logo) {
          setLogoUrl(`data:${logoRes.data.content_type};base64,${logoRes.data.data}`);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const getTeamById = (id) => teams.find(t => t.id === id);

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section 
        className="hero-section flex items-center justify-center min-h-[100svh]"
        style={{ backgroundImage: `url(${HERO_IMAGE})` }}
      >
        <div className="hero-overlay" />
        <div className="relative z-10 text-center px-4 py-6 sm:py-8 md:py-12 w-full max-w-4xl mx-auto">
          
          {/* Logo del Torneo */}
          <div className="mb-4 sm:mb-5 md:mb-6">
            <img 
              src={logoUrl} 
              alt="Logo Torneo Notturno di Calcio" 
              className="w-28 h-28 sm:w-36 sm:h-36 md:w-44 md:h-44 mx-auto drop-shadow-2xl"
            />
          </div>

          {/* 30th Edition Badge */}
          <div className="mb-2 sm:mb-3 md:mb-4">
            <div className="inline-flex items-center gap-2 px-3 sm:px-4 md:px-6 py-1.5 sm:py-2 bg-gradient-to-r from-gold/20 to-amber-600/20 border-2 border-gold/50 rounded-full animate-pulse-glow">
              <Star className="w-4 h-4 sm:w-5 sm:h-5 text-gold fill-gold" />
              <span className="text-sm sm:text-base md:text-xl font-heading font-bold text-gold uppercase tracking-wider">
                30ª Edizione
              </span>
              <Star className="w-4 h-4 sm:w-5 sm:h-5 text-gold fill-gold" />
            </div>
          </div>
          
          {/* Title */}
          <h1 className="font-heading text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tighter mb-1 sm:mb-2 leading-none">
            TORNEO <span className="text-primary block sm:inline">NOTTURNO</span> VIADANA
          </h1>
          <h2 className="font-heading text-lg sm:text-xl md:text-2xl lg:text-3xl font-semibold text-slate-300 mb-1">
            CALCIO A 6 · 30ª EDIZIONE
          </h2>
          <p className="text-sm sm:text-base md:text-lg text-slate-400 mb-3 sm:mb-4 md:mb-6">
            Viadana Bresciana - Edizione 2026 conclusa
          </p>

          {/* Torneo concluso banner */}
          <div className="mb-4 sm:mb-6 max-w-2xl mx-auto px-4">
            <div className="bg-gradient-to-r from-gold/10 via-gold/20 to-gold/10 border-2 border-gold/50 rounded-2xl p-5 sm:p-6 md:p-8 backdrop-blur-sm">
              <div className="flex items-center justify-center gap-2 mb-3">
                <Trophy className="w-6 h-6 sm:w-7 sm:h-7 text-gold" />
                <h3 className="font-heading text-xl sm:text-2xl md:text-3xl font-bold text-gold uppercase">
                  Torneo Concluso
                </h3>
                <Trophy className="w-6 h-6 sm:w-7 sm:h-7 text-gold" />
              </div>
              <p className="text-sm sm:text-base md:text-lg text-slate-200 leading-relaxed mb-2">
                La 30ª edizione è ufficialmente terminata.<br className="hidden sm:block"/>
                Grazie di cuore a <strong className="text-white">squadre, giocatori, sponsor, volontari e tifosi</strong> che hanno reso magiche queste serate.
              </p>
              <p className="text-base sm:text-lg md:text-xl font-heading font-semibold text-primary mt-3">
                Ci vediamo l&apos;anno prossimo! ⚽
              </p>
            </div>
          </div>

          {/* CTA Buttons - Albo d'Oro / Galleria */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3 mt-4 sm:mt-5 md:mt-6 px-4">
            <Link to="/albo-doro" className="w-full sm:w-auto">
              <Button
                className="w-full sm:w-auto btn-skewed bg-gold text-slate-950 hover:bg-gold/90 px-5 sm:px-6 md:px-8 py-2.5 sm:py-3 md:py-4 text-sm sm:text-base"
                data-testid="cta-albo"
              >
                <span>Vedi l&apos;Albo d&apos;Oro</span>
              </Button>
            </Link>
            <Link to="/galleria" className="w-full sm:w-auto">
              <Button
                variant="outline"
                className="w-full sm:w-auto btn-skewed border-2 border-slate-700 hover:border-primary px-5 sm:px-6 md:px-8 py-2.5 sm:py-3 md:py-4 text-sm sm:text-base"
                data-testid="cta-galleria-home"
              >
                <span>Galleria foto</span>
              </Button>
            </Link>
          </div>
        </div>
        
        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce hidden md:block">
          <div className="w-6 h-10 border-2 border-slate-600 rounded-full flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-primary rounded-full animate-pulse"></div>
          </div>
        </div>
      </section>

      {/* 30th Edition Section */}
      <section className="py-12 md:py-16 px-4 bg-gradient-to-b from-slate-950 via-gold/5 to-slate-950 border-y border-gold/20">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex justify-center mb-4 md:mb-6">
            <div className="p-3 md:p-4 bg-gold/10 rounded-full border border-gold/30">
              <Trophy className="w-8 h-8 md:w-12 md:h-12 text-gold" />
            </div>
          </div>
          <h3 className="font-heading text-2xl md:text-4xl font-bold mb-3 md:mb-4">
            <span className="text-gold">30 ANNI</span> DI STORIA
          </h3>
          <p className="text-slate-400 text-sm md:text-lg max-w-2xl mx-auto leading-relaxed px-4">
            Dal 1995, il Torneo Notturno di Viadana Bresciana unisce la comunità 
            in una celebrazione di sport, amicizia e tradizione.
          </p>
          <div className="mt-6 md:mt-8 flex flex-wrap justify-center gap-4 md:gap-8 text-center">
            <div>
              <p className="font-numbers text-3xl md:text-5xl font-bold text-gold">30</p>
              <p className="text-xs md:text-sm text-slate-500 uppercase tracking-wider">Edizioni</p>
            </div>
            <div className="hidden sm:block w-px bg-slate-800"></div>
            <div>
              <p className="font-numbers text-3xl md:text-5xl font-bold text-white">12</p>
              <p className="text-xs md:text-sm text-slate-500 uppercase tracking-wider">Squadre</p>
            </div>
            <div className="hidden sm:block w-px bg-slate-800"></div>
            <div>
              <p className="font-numbers text-3xl md:text-5xl font-bold text-primary">1</p>
              <p className="text-xs md:text-sm text-slate-500 uppercase tracking-wider">Campione</p>
            </div>
          </div>
        </div>
      </section>

      {/* Instagram Feed Section */}
      <section className="py-12 md:py-16 px-4 bg-slate-900/30">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-3 mb-4">
              <svg className="w-6 h-6 md:w-8 md:h-8 text-pink-500" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
              <h2 className="font-heading text-xl md:text-2xl lg:text-3xl font-bold">
                SEGUICI SU <span className="text-pink-500">INSTAGRAM</span>
              </h2>
            </div>
            <p className="text-slate-400 text-sm md:text-base mb-6">
              Foto e video delle nostre edizioni
            </p>
          </div>

          {/* Instagram Embed */}
          <div className="flex justify-center">
            <div className="w-full max-w-lg">
              <blockquote 
                className="instagram-media" 
                data-instgrm-permalink="https://www.instagram.com/torneo_notturno_viadana/"
                data-instgrm-version="14"
                style={{
                  background: '#1e293b',
                  border: 0,
                  borderRadius: '12px',
                  margin: '0 auto',
                  maxWidth: '540px',
                  minWidth: '326px',
                  padding: 0,
                  width: '100%'
                }}
              >
                <div className="p-8 text-center">
                  <a 
                    href="https://www.instagram.com/torneo_notturno_viadana/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block"
                  >
                    <div className="flex items-center justify-center mb-4">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600 p-0.5">
                        <div className="w-full h-full rounded-full bg-slate-900 flex items-center justify-center">
                          <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073z"/>
                          </svg>
                        </div>
                      </div>
                    </div>
                    <p className="font-heading font-bold text-lg text-white mb-1">@torneo_notturno_viadana</p>
                    <p className="text-slate-400 text-sm mb-4">Torneo Notturno Viadana Bresciana</p>
                    <span className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg font-semibold text-white hover:from-purple-500 hover:to-pink-500 transition-all">
                      Visualizza profilo Instagram
                    </span>
                  </a>
                </div>
              </blockquote>
            </div>
          </div>
        </div>
      </section>

      {/* Upcoming Matches - nascosto: torneo concluso */}
      {false && settings?.draw_completed && (
        <section className="py-12 md:py-16 px-4">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center justify-between mb-6 md:mb-8">
              <h2 className="font-heading text-xl md:text-2xl lg:text-3xl font-bold">
                PROSSIME <span className="text-primary">PARTITE</span>
              </h2>
              <Link
                to="/calendario"
                className="flex items-center gap-1 text-xs md:text-sm text-slate-400 hover:text-primary transition-colors"
              >
                Vedi tutte <ChevronRight className="w-4 h-4" />
              </Link>
            </div>

            {upcomingMatches.length > 0 ? (
              <div className="grid gap-3 md:gap-4 md:grid-cols-2">
                {upcomingMatches.map(match => (
                  <MatchCard
                    key={match.id}
                    match={match}
                    homeTeam={getTeamById(match.home_team_id)}
                    awayTeam={getTeamById(match.away_team_id)}
                  />
                ))}
              </div>
            ) : (
              <div className="glass-card p-8 md:p-12 text-center">
                <Calendar className="w-10 h-10 md:w-12 md:h-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400 text-sm md:text-base">
                  {loading ? 'Caricamento...' : 'Il calendario non è ancora stato generato'}
                </p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Albo d'Oro Preview */}
      {champions.length > 0 && (
        <section className="py-12 md:py-16 px-4 bg-gradient-to-b from-slate-950 via-gold/5 to-slate-950 border-y border-gold/10" data-testid="home-albo-doro-section">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-6 md:mb-8">
              <div>
                <span className="inline-flex items-center gap-2 px-3 py-1 text-xs font-semibold text-gold border border-gold/30 rounded-full mb-2 uppercase tracking-wider">
                  <Trophy className="w-3 h-3" /> Albo d'Oro
                </span>
                <h2 className="font-heading text-xl md:text-2xl lg:text-3xl font-bold">
                  I <span className="text-gold">VINCITORI</span> DELLE EDIZIONI PASSATE
                </h2>
              </div>
              <Link
                to="/albo-doro"
                className="flex items-center gap-1 text-xs md:text-sm text-slate-400 hover:text-gold transition-colors"
                data-testid="home-albo-doro-cta"
              >
                Vedi tutti <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
              {champions.slice(0, 4).map(c => (
                <Link
                  key={c.id}
                  to="/albo-doro"
                  className="group rounded-xl overflow-hidden border border-slate-800 bg-slate-900/60 hover:border-gold/40 transition-all"
                  data-testid={`home-champ-${c.id}`}
                >
                  <div className="aspect-[4/3] bg-slate-950 overflow-hidden">
                    {c.photo_data ? (
                      <img
                        src={`data:${c.photo_content_type};base64,${c.photo_data}`}
                        alt={`${c.team_name} - ${c.year}`}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-700">
                        <Trophy className="w-12 h-12" />
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    <p className="font-numbers text-lg font-bold text-gold leading-none">{c.year}</p>
                    {c.team_name ? (
                      <p className="font-heading text-sm font-bold truncate mt-1">{c.team_name}</p>
                    ) : null}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Galleria foto partite Preview */}
      {photos.length > 0 && (
        <section className="py-12 md:py-16 px-4" data-testid="home-galleria-section">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-6 md:mb-8">
              <div>
                <span className="inline-flex items-center gap-2 px-3 py-1 text-xs font-semibold text-primary border border-primary/30 rounded-full mb-2 uppercase tracking-wider">
                  <Camera className="w-3 h-3" /> Galleria
                </span>
                <h2 className="font-heading text-xl md:text-2xl lg:text-3xl font-bold">
                  FOTO DELLE <span className="text-primary">PARTITE</span>
                </h2>
              </div>
              <Link
                to="/galleria"
                className="flex items-center gap-1 text-xs md:text-sm text-slate-400 hover:text-primary transition-colors"
                data-testid="home-galleria-cta"
              >
                Vedi tutte <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3">
              {photos.slice(0, 8).map(p => (
                <Link
                  key={p.id}
                  to="/galleria"
                  className="group aspect-square rounded-xl overflow-hidden border border-slate-800 hover:border-primary/40 transition-all"
                  data-testid={`home-photo-${p.id}`}
                >
                  <img
                    src={`data:${p.photo_content_type};base64,${p.photo_data}`}
                    alt={p.title || 'Foto partita'}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    loading="lazy"
                  />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* SEO content (visibile, leggero) */}
      <section className="py-10 px-4 bg-slate-950 border-t border-slate-900" aria-label="Informazioni sul torneo">
        <div className="max-w-4xl mx-auto text-center text-slate-400 text-sm md:text-base leading-relaxed space-y-3">
          <h3 className="font-heading text-xl md:text-2xl font-bold text-white mb-2">
            Uno dei tornei di calcio estivi storici del territorio
          </h3>
          <p>
            Il <strong>Torneo Notturno di Viadana Bresciana</strong> è un torneo di <strong>calcio a 6</strong>
            giocato ogni estate a Viadana, in provincia di Brescia.
            Una manifestazione sportiva amatoriale che dal 1995 raccoglie squadre del territorio bresciano
            per 30 edizioni di calcio, divertimento e amicizia sotto le stelle.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 bg-slate-950 border-t border-slate-800">
        <div className="max-w-4xl mx-auto text-center space-y-3">
          <nav className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-slate-500" aria-label="Link rapidi">
            <Link to="/albo-doro" className="hover:text-gold transition-colors" data-testid="footer-albo-doro">
              Albo d'Oro
            </Link>
            <span className="text-slate-700">·</span>
            <Link to="/galleria" className="hover:text-primary transition-colors" data-testid="footer-galleria">
              Galleria foto
            </Link>
            <span className="text-slate-700">·</span>
            <Link to="/sponsor" className="hover:text-primary transition-colors">
              Sponsor
            </Link>
            <span className="text-slate-700">·</span>
            <Link to="/menu" className="hover:text-primary transition-colors">
              Menu
            </Link>
          </nav>
          <p className="text-slate-500 text-sm">
            Realizzato da <a href="https://strategyfor.it" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary/80 font-semibold transition-colors">Strategyfor srl</a>
          </p>
          <p className="text-slate-600 text-xs">
            © 2026 Torneo Notturno Viadana Bresciana - 30ª Edizione
          </p>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
